"use client";

import type { DateFormat } from "@/features/profile-setup/_logic/constants";
import { useDisplayPreferences } from "@/features/profile-setup/_hooks/useDisplayPreferences";
import { createContext, type ReactNode } from "react";

/** `null` outside the provider; `useDateFormat` then falls back to `DEFAULT_DATE_FORMAT`. */
export const DateFormatContext = createContext<DateFormat | null>(null);

interface DateFormatProviderProps {
  /** The format the server resolved for this request: saved, else suggested by `Accept-Language`. */
  initialDateFormat: DateFormat;
  children: ReactNode;
}

/**
 * Supplies the user's date format to the subtree.
 *
 * Renders with the server-resolved format and keeps it over the client session's cached
 * metadata, which can predate a save made on another device. Only a save after mount
 * (`USER_UPDATED`) replaces it, so dates re-render without a reload.
 *
 * @param props.initialDateFormat - The format resolved on the server (see `resolveRequestDateFormat`).
 * @param props.children - Subtree that reads the format via `useDateFormat`.
 * @returns Provider element wrapping `children`.
 */
export const DateFormatProvider = ({ initialDateFormat, children }: DateFormatProviderProps) => {
  // Hooks
  const { saved, isUpdated } = useDisplayPreferences();

  // Derived values
  const dateFormat = (isUpdated ? saved.dateFormat : undefined) ?? initialDateFormat;

  // Render
  return <DateFormatContext.Provider value={dateFormat}>{children}</DateFormatContext.Provider>;
};
